import type {
  AiHandoffBuildingPayload,
  AiHandoffFigure,
  BuildingAnalysisSectionId,
} from '@popety-io/types'
import { buildSections, emptySections, specIds } from './sectionValues'
import { BUILDING_SECTION_SPECS } from './buildingSectionFields'
import { aiHandoffAbout } from './about'
import { AI_HANDOFF_CONNECT_URL } from './constants'

const ALL_SECTION_IDS: BuildingAnalysisSectionId[] = [
  'summary',
  'history',
  'characteristics',
  'units',
  'energy',
  'land',
  'potential',
  'building_permits',
  'feasibility',
  'purchase_market',
  'rental_market',
  'purchase_listings',
  'rental_listings',
  'transactions',
  'transport',
  'amenities',
  'noise',
  'sunlight',
  'demographics',
  'employment',
  'taxes',
  'electricity',
  'notes',
  'files',
]

/** `summary` is emitted through `identity`, not as a section of its own. */
const SUPPORTED_SECTIONS: BuildingAnalysisSectionId[] = [
  'summary',
  ...specIds<BuildingAnalysisSectionId>(BUILDING_SECTION_SPECS),
]

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const str = (v: unknown): string | null =>
  typeof v === 'string' && v ? v : null

const figure = (
  v: unknown,
  unit: AiHandoffFigure['unit'],
  asOf: string,
  round: (n: number) => number
): AiHandoffFigure | null => {
  const value = num(v)
  return value === null
    ? null
    : { value: round(value), unit, source: 'geoportal', as_of: asOf }
}

/**
 * A building share stores its selection as an OBJECT keyed by section id
 * (`{"summary":true}`), not as an array like a land share. Only keys whose
 * value is literally `true` count as selected — a stored `false` means the
 * customer deselected it.
 */
const selectedFrom = (sections: unknown): BuildingAnalysisSectionId[] => {
  if (Array.isArray(sections)) return sections as BuildingAnalysisSectionId[]
  if (typeof sections !== 'object' || sections === null) return []
  return Object.entries(sections as Record<string, unknown>)
    .filter(([, v]) => v === true)
    .map(([k]) => k as BuildingAnalysisSectionId)
}

export const buildBuildingAiPayload = (
  share: {
    value?: unknown
    sections?: unknown
    building_id?: string | number
  },
  opts: { publicUrl: string; asOf: string }
): AiHandoffBuildingPayload => {
  const { asOf, publicUrl } = opts
  const value = (share.value ?? {}) as Record<string, any>
  const summary = (value.summary ?? {}) as Record<string, unknown>
  // `header` is written onto every building share regardless of the sections
  // chosen (23 of 23 in production), so it is the always-available identity
  // context. `summary` is read only as a fallback for fields header lacks.
  const header = (value.header ?? {}) as Record<string, unknown>

  const requested = selectedFrom(share.sections)
  const recognised = requested.filter((s) => ALL_SECTION_IDS.includes(s))
  const included = recognised.filter((s) => SUPPORTED_SECTIONS.includes(s))
  const omitted = recognised.filter((s) => !SUPPORTED_SECTIONS.includes(s))

  const coords = (header.geo_center ?? summary.geo_center) as
    { lat?: unknown; lon?: unknown } | undefined
  const lat = num(coords?.lat)
  const lon = num(coords?.lon)
  const round1 = (n: number): number => Math.round(n * 10) / 10

  const sections = buildSections(value, included, asOf, BUILDING_SECTION_SPECS)

  return {
    schema_version: '1.0',
    about: aiHandoffAbout('building'),
    artifact: 'building',
    as_of: asOf,
    attribution: {
      source: 'Popety.io',
      url: publicUrl,
      connect_url: AI_HANDOFF_CONNECT_URL,
    },
    sections_included: included,
    sections_omitted: omitted,
    sections_empty: emptySections(included, sections, BUILDING_SECTION_SPECS),
    identity: {
      building_id:
        str(header.building_id ?? summary.building_id) ??
        share.building_id?.toString() ??
        null,
      egid: num(header.egid ?? summary.egid),
      address_full: str(header.address_full ?? summary.address_full),
      municipality_name: str(
        header.municipality_name ?? summary.municipality_name
      ),
      district_name: str(summary.district_name),
      coordinates: lat !== null && lon !== null ? { lat, lon } : null,
      building_category: str(header.category ?? summary.category),
      building_type: str(summary.type),
      building_status: str(summary.status),
      popety_classification: str(header.popety_classification),
      building_year: num(header.building_year ?? summary.building_year),
      renovation_year: num(summary.renovation_year),
      floor_nb: num(header.floor_nb ?? summary.floor_nb),
      unit_nb: num(header.unit_nb ?? summary.unit_nb),
      dwellings_nb: num(summary.dwellings_nb),
      height: figure(summary.height, 'm', asOf, round1),
      volume: figure(summary.building_volume, 'm3', asOf, Math.round),
      gross_floor_area: figure(summary.gross_floor_area, 'm2', asOf, round1),
    },
    sections,
  }
}
